/**
 * Composant affichant l'analyse approfondie en 3 colonnes
 * Points forts / Axes d'amélioration / Conseils manager
 */

interface InsightItem {
  titre: string;
  description: string;
}

interface ThreeColumnInsightsProps {
  resume?: string;
  pointsForts: InsightItem[];
  axesAmelioration: InsightItem[];
  conseilsManager: InsightItem[];
}

export const ThreeColumnInsights = ({
  resume,
  pointsForts,
  axesAmelioration,
  conseilsManager
}: ThreeColumnInsightsProps) => {
  
  // Fonction pour rendre une liste d'items
  const renderItems = (items: InsightItem[]) => {
    if (items.length === 0) {
      return (
        <div className="self-stretch p-4 bg-gray-50 rounded-lg flex flex-col justify-center items-start gap-1">
          <div className="justify-center text-gray-400 text-sm font-normal font-['Bricolage_Grotesque'] leading-5">
            Aucune donnée disponible
          </div>
        </div>
      );
    }

    return items.map((item, index) => (
      <div 
        key={index}
        className="self-stretch p-4 bg-gray-50 rounded-lg flex flex-col justify-center items-start gap-1"
      >
        <div className="justify-center text-teal-400 text-base font-medium font-['Bricolage_Grotesque'] leading-5">
          {item.titre}
        </div>
        <div className="self-stretch justify-center text-gray-600 text-sm font-normal font-['Bricolage_Grotesque'] leading-5">
          {item.description}
        </div>
      </div>
    ));
  };

  return (
    <div className="w-full flex flex-col gap-8">
      {/* Section 1 : Analyse approfondie + Résumé */}
      <div className="w-full p-4 bg-gray-50 rounded-[10px] outline outline-1 outline-offset-[-1px] outline-neutral-400/20 inline-flex flex-col justify-center items-start gap-1">
        {/* Titre principal */}
        <div className="justify-start text-gray-800 text-xl font-medium font-['Bricolage_Grotesque']">
          Analyse approfondie
        </div>

        {/* Section Résumé (optionnelle) */}
        {resume && (
          <div className="self-stretch flex flex-col justify-start items-start gap-0.5">
            <div className="justify-start text-teal-400 text-sm font-medium font-['Bricolage_Grotesque']">
              Résumé
            </div>
            <div className="self-stretch justify-start text-neutral-400 text-sm font-normal font-['Bricolage_Grotesque']">
              {resume}
            </div>
          </div>
        )}
      </div>

      {/* Section 2 : 3 Colonnes */}
      <div className="self-stretch flex justify-start items-stretch gap-4">
        {/* Colonne 1 : Points forts identifiés */}
        <div className="flex-1 p-5 bg-white rounded-lg border border-gray-200 flex flex-col justify-start items-start gap-3">
          {/* En-tête */}
          <div className="self-stretch inline-flex justify-start items-center gap-3">
            <div className="size-9 p-2 bg-green-500/10 rounded-lg inline-flex flex-col justify-center items-center gap-2.5 overflow-hidden">
              <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M5.8346 16.6667L8.96793 17.45C9.10011 17.4831 9.23584 17.4999 9.3721 17.5H12.7204C13.03 17.5 13.3335 17.4138 13.5968 17.251C13.8601 17.0882 14.0729 16.8553 14.2113 16.5783L17.1279 10.745C17.2549 10.4909 17.3148 10.2086 17.302 9.9248C17.2892 9.64104 17.2041 9.36525 17.0547 9.12364C16.9053 8.88202 16.6967 8.68259 16.4486 8.54427C16.2005 8.40595 15.9212 8.33334 15.6371 8.33333H11.6671H10.0004M5.8346 16.6667L5.83377 9.16667L8.66043 4.92667C8.99127 4.43083 9.1671 3.84917 9.1671 3.25333C9.1671 2.8375 9.5046 2.5 9.92127 2.5H10.0013C10.4433 2.5 10.8672 2.6756 11.1798 2.98816C11.4923 3.30072 11.6679 3.72464 11.6679 4.16667V8.33333M5.8346 16.6667H4.1671C3.72507 16.6667 3.30115 16.4911 2.98859 16.1785C2.67603 15.866 2.50043 15.442 2.50043 15V10C2.50043 9.55797 2.67603 9.13405 2.98859 8.82149C3.30115 8.50893 3.72507 8.33333 4.1671 8.33333H6.25043" stroke="#32CD32" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </div>
            <div className="justify-start text-gray-800 text-lg font-medium font-['Bricolage_Grotesque'] leading-5">
              Points forts identifiés
            </div>
          </div>

          {/* Liste des items */}
          <div className="self-stretch flex flex-col justify-start items-start gap-3">
            {renderItems(pointsForts)}
          </div>
        </div>

        {/* Colonne 2 : Axes d'améliorations */}
        <div className="flex-1 p-5 bg-white rounded-lg border border-gray-200 flex flex-col justify-start items-start gap-3">
          {/* En-tête */}
          <div className="self-stretch inline-flex justify-start items-center gap-3">
            <div className="size-9 p-2 bg-rose-500/10 rounded-lg inline-flex flex-col justify-center items-center gap-2.5 overflow-hidden">
              <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
                <g clipPath="url(#clip0_1215_7083)">
                  <path d="M9.5 3.5H13.5V7.5" stroke="#FF515D" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M13.5 3.5L7.85 9.15C7.75654 9.24161 7.63088 9.29293 7.5 9.29293C7.36912 9.29293 7.24346 9.24161 7.15 9.15L4.85 6.85C4.75654 6.75839 4.63088 6.70707 4.5 6.70707C4.36912 6.70707 4.24346 6.75839 4.15 6.85L0.5 10.5" stroke="#FF515D" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                </g>
                <defs>
                  <clipPath id="clip0_1215_7083">
                    <rect width="14" height="14" fill="white"/>
                  </clipPath>
                </defs>
              </svg>
            </div>
            <div className="justify-start text-gray-800 text-lg font-medium font-['Bricolage_Grotesque'] leading-5">
              Axes d'améliorations
            </div>
          </div>

          {/* Liste des items */}
          <div className="self-stretch flex flex-col justify-start items-start gap-3">
            {renderItems(axesAmelioration)}
          </div>
        </div>

        {/* Colonne 3 : Conseils à son futur manager */}
        <div className="flex-1 p-5 bg-white rounded-lg border border-gray-200 flex flex-col justify-start items-start gap-3">
          {/* En-tête */}
          <div className="self-stretch inline-flex justify-start items-center gap-3">
            <div className="size-9 p-2 bg-teal-400/10 rounded-lg inline-flex flex-col justify-center items-center gap-2.5 overflow-hidden">
              <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M9.99954 2.5L8.04121 7.3L2.86621 7.68333L6.83288 11.0333L5.59121 16.0667L9.99954 13.3333M9.99954 2.5L11.9579 7.3L17.1329 7.68333L13.1662 11.0333L14.4079 16.0667L9.99954 13.3333" stroke="#40E0D0" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </div>
            <div className="justify-start text-gray-800 text-lg font-medium font-['Bricolage_Grotesque'] leading-5">
              Conseils à son futur manager
            </div>
          </div>

          {/* Liste des items */}
          <div className="self-stretch flex flex-col justify-start items-start gap-3">
            {renderItems(conseilsManager)}
          </div>
        </div>
      </div>
    </div>
  );
};
